import type { NodeState, NodeStatus, Pipe, PipeStatus, Prospect, Rejection } from './contract'

/* Regras da régua, as mesmas no servidor (que decide) e na tela (que mostra). Puras: recebem e devolvem objetos novos.
   Uma régua = fases em ordem; uma delas é o "portão" (retorno do prospect). Fases `afterAccept` ficam bloqueadas até o
   aceite: dá para revisar e aprovar o conteúdo antes, mas só marcar como enviado depois. */

export type Channel =
    | 'WhatsApp' | 'E-mail' | 'PDF' | 'Landing page' | 'Reunião' | 'Pesquisa' | 'Evento' | 'Visita' | 'Kit físico'
    | 'LinkedIn' | 'Instagram DM' | 'Conteúdo'

/** Cor do ponto de cada canal (mesmos tons dos protótipos, lidos sobre o fundo do panel). */
export const CHANNEL_COLORS: Record<Channel, string> = {
    WhatsApp: '#25A35A', 'E-mail': '#3B5BA9', PDF: '#C0532B', 'Landing page': '#0B7A3B', Reunião: '#6F6B62',
    Pesquisa: '#8A7F6B', Evento: '#8E5BB5', Visita: '#2F8F9D', 'Kit físico': '#E0A100', LinkedIn: '#0A66C2',
    'Instagram DM': '#C13584', Conteúdo: '#8E5BB5',
}

/** Rótulo do campo de texto no painel do conteúdo, por canal. */
export function bodyLabel(channel: Channel): string {
    switch (channel) {
        case 'E-mail': return 'Corpo do e-mail'
        case 'Reunião': return 'Pauta'
        case 'Pesquisa': return 'Roteiro de pesquisa'
        case 'Kit físico': return 'Itens do kit'
        case 'Evento':
        case 'Visita': return 'Roteiro'
        case 'PDF': return 'Estrutura do documento'
        case 'Landing page': return 'Conteúdo da página'
        default: return 'Mensagem'
    }
}

export type JourneyNode = {
    id: string
    channel: Channel
    title: string
    /** "D+0", "A+15", "Trimestral"… só rótulo, nada é agendado. */
    when: string
    objective: string
    /** E-mails: assunto com marcadores ({e}, {p}…); null = sem assunto. */
    subject: string | null
    /** Rascunho do agente, com marcadores (cada tipo diz quais em `fill`). */
    body: string
}

export type JourneyPhase = {
    id: string
    title: string
    window: string
    /** Bloqueada até o aceite (pode aprovar antes; só envia depois). */
    afterAccept?: boolean
    /** Marcar como enviado um conteúdo desta fase leva "abordagem" para "aguardando". */
    movesToWaiting?: boolean
    nodes: JourneyNode[]
}

export type Journey = {
    /** Fases em ordem; `gateAfter` = id da fase depois da qual vem o portão do retorno. */
    phases: JourneyPhase[]
    gateAfter: string
    gate: { title: string; window: string; acceptLabel: string; refuseLabel: string; refuseReasons: string[] }
    /** Rótulos dos status na régua de cada tipo. */
    statusLabels: Record<PipeStatus, string>
    /** Colunas do quadro: título e subtítulo por status (recusou não tem coluna). */
    columns: Record<Exclude<PipeStatus, 'recusou'>, { title: string; sub: string }>
    /** Texto quando não há próximo conteúdo: aguardando / outros. */
    nextWhenDone: { waiting: string; other: string }
    /** Quem decide, nas mensagens ("responsável", "marketing"). */
    decider: string
}

export const NODE_STATUS_LABELS: Record<NodeStatus, string> = { rascunho: 'Rascunho do agente', aprovado: 'Aprovado · pronto', enviado: 'Enviado' }

const EMPTY_NODE: NodeState = { st: 'rascunho', at: null, subj: null, body: null, ag: false, ed: false }

export const allNodes = (j: Journey) => j.phases.flatMap(p => p.nodes.map(n => ({ phase: p, node: n })))
export const findNode = (j: Journey, nodeId: string) => allNodes(j).find(x => x.node.id === nodeId) ?? null
export const nodeState = (pipe: Pipe | null, nodeId: string): NodeState => pipe?.nodes[nodeId] ?? EMPTY_NODE

export const isLocked = (phase: JourneyPhase, pipe: Pipe | null) => !!phase.afterAccept && pipe?.st !== 'ativo'

/** Conteúdos disponíveis: os das fases livres; depois do aceite, todos. */
export const availableNodes = (j: Journey, pipe: Pipe | null) => allNodes(j).filter(x => !isLocked(x.phase, pipe))

export function progress(j: Journey, pipe: Pipe | null) {
    const avail = availableNodes(j, pipe)
    const all = allNodes(j)
    const st = (id: string) => nodeState(pipe, id).st
    return {
        done: avail.filter(x => st(x.node.id) === 'enviado').length,
        total: avail.length,
        /** Rascunhos entre os disponíveis (o protótipo contava os bloqueados também; aqui é coerente com os outros). */
        toApprove: avail.filter(x => st(x.node.id) === 'rascunho').length,
        ready: avail.filter(x => st(x.node.id) === 'aprovado').length,
        sent: avail.filter(x => st(x.node.id) === 'enviado').length,
        all: all.length,
    }
}

export function nextStep(j: Journey, pipe: Pipe | null): string {
    const next = availableNodes(j, pipe).find(x => nodeState(pipe, x.node.id).st !== 'enviado')
    if (next) return next.node.title
    return pipe?.st === 'aguardando' ? j.nextWhenDone.waiting : j.nextWhenDone.other
}

/** Marcadores {chave} trocados pelos valores; o que não existe fica como está ([nome] etc. ficam para a pessoa). */
export function fillTemplate(text: string, values: Record<string, string>): string {
    return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? values[k]! : m))
}

/** "Igreja Batista Esperança" -> "igreja-batista-esperanca". */
export function slugOf(name: string): string {
    return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// --- Transições (o servidor aplica; erros viram 400) ---

export class RuleError extends Error {}

export const newPipe = (today: string, withImpact: boolean): Pipe => ({
    st: 'abordagem', at: today, acceptedAt: null, refusal: null, impact: withImpact ? { p: 0, c: 0, n: 0 } : null, news: [], nodes: {},
})

/** Aprovar a sugestão: começa a régua (uma régua que já existe é mantida). */
export function approve(p: Prospect, today: string): Prospect {
    return { ...p, rej: null, pipe: p.pipe && p.pipe.st !== 'recusou' ? p.pipe : newPipe(today, p.kind === 'ambassador') }
}

export function reject(p: Prospect, today: string, motivo: string, txt: string): Prospect {
    return { ...p, pipe: null, rej: { at: today, motivo: motivo || 'Sem motivo informado', txt, by: 'mk' } }
}

/** Volta a ser sugestão sem decisão. */
export function reconsider(p: Prospect): Prospect {
    return { ...p, rej: null, pipe: p.pipe?.st === 'recusou' ? null : p.pipe }
}

export function applyReturn(p: Prospect, today: string, action: 'accept' | 'noanswer' | 'refuse' | 'reopen', r: { motivo: string; txt: string; data?: string }): Prospect {
    const pipe = p.pipe
    if (!pipe) throw new RuleError('sem régua')
    switch (action) {
        case 'accept':
            if (pipe.st === 'recusou') throw new RuleError('reabra antes')
            return { ...p, pipe: { ...pipe, st: 'ativo', acceptedAt: today, refusal: null } }
        case 'noanswer':
            if (pipe.st === 'recusou' || pipe.st === 'ativo') throw new RuleError('já tem retorno')
            return { ...p, pipe: { ...pipe, st: 'aguardando' } }
        case 'refuse': {
            const data = r.data ?? today
            const motivo = r.motivo || 'Sem motivo informado'
            const rej: Rejection = { at: data, motivo, txt: r.txt, by: 'retorno' }
            return { ...p, rej, pipe: { ...pipe, st: 'recusou', acceptedAt: null, refusal: { motivo, txt: r.txt, data } } }
        }
        case 'reopen':
            return { ...p, rej: null, pipe: { ...pipe, st: 'aguardando', acceptedAt: null, refusal: null } }
    }
}

/**
 * Operação num conteúdo. `defaults` = texto do modelo já preenchido (para guardar a versão aprovada).
 * edit: guarda o texto, marca "editado"; se estava aprovado volta a rascunho (enviado não edita).
 * approve: rascunho -> aprovado. send: aprovado -> enviado (só fora de fase bloqueada). draft: volta a rascunho.
 */
export function applyNode(
    p: Prospect,
    j: Journey,
    today: string,
    nodeId: string,
    op: 'edit' | 'approve' | 'send' | 'draft',
    texts: { subj?: string; body?: string },
    defaults: { subj: string | null; body: string },
): Prospect {
    const pipe = p.pipe
    if (!pipe) throw new RuleError('sem régua')
    const found = findNode(j, nodeId)
    if (!found) throw new RuleError('conteúdo desconhecido')
    const cur = nodeState(pipe, nodeId)
    const materialize = (n: NodeState): NodeState => ({ ...n, subj: n.subj ?? defaults.subj, body: n.body ?? defaults.body })
    let next: NodeState
    let st: PipeStatus = pipe.st
    switch (op) {
        case 'edit':
            if (cur.st === 'enviado') throw new RuleError('conteúdo já enviado')
            next = {
                ...materialize(cur),
                ...(texts.subj !== undefined ? { subj: texts.subj } : {}),
                ...(texts.body !== undefined ? { body: texts.body } : {}),
                st: 'rascunho', ed: true,
            }
            break
        case 'approve':
            if (cur.st !== 'rascunho') throw new RuleError('só rascunho é aprovado')
            next = { ...materialize(cur), st: 'aprovado' }
            break
        case 'send':
            if (cur.st !== 'aprovado') throw new RuleError('só conteúdo aprovado é enviado')
            if (isLocked(found.phase, pipe)) throw new RuleError('envio libera após o aceite')
            next = { ...cur, st: 'enviado', at: today }
            if (pipe.st === 'abordagem' && found.phase.movesToWaiting) st = 'aguardando'
            break
        case 'draft':
            next = { ...cur, st: 'rascunho', at: null }
            break
    }
    return { ...p, pipe: { ...pipe, st, nodes: { ...pipe.nodes, [nodeId]: next } } }
}

/** Texto do agente: volta a rascunho, "reescrito pelo agente". */
export function applyRewrite(p: Prospect, nodeId: string, body: string): Prospect {
    const pipe = p.pipe
    if (!pipe) throw new RuleError('sem régua')
    const cur = nodeState(pipe, nodeId)
    if (cur.st === 'enviado') throw new RuleError('conteúdo já enviado')
    return { ...p, pipe: { ...pipe, nodes: { ...pipe.nodes, [nodeId]: { ...cur, body, st: 'rascunho', ag: true, ed: false } } } }
}

/** Situação da decisão do responsável sobre a sugestão. */
export function decisionOf(p: Pick<Prospect, 'pipe' | 'rej'>): 'undecided' | 'approved' | 'rejected' {
    if (p.rej) return 'rejected'
    return p.pipe ? 'approved' : 'undecided'
}
